import { supabase } from '../utils/supabase';

export interface MovementAnalytics {
  success: boolean;
  auth_method: string;
  date_range: {
    start_date: string;
    end_date: string;
  };
  summary: {
    total_items: number;
    no_movement_items: number;
    slow_moving_items: number;
    medium_moving_items: number;
    fast_moving_items: number;
    no_movement_percentage: number;
    slow_moving_percentage: number;
    medium_moving_percentage: number;
    fast_moving_percentage: number;
  };
}

export interface MovementCategoryItem {
  item_id: string;
  item_name: string;
  sku: string;
  barcode: string;
  category: string;
  brand: string;
  current_stock: number;
  average_cost: number;
  total_value: number;
  total_movements: number;
  total_quantity_moved: number;
  last_movement_date: string;
  days_since_last_movement: number;
  movement_category: 'no_movement' | 'slow_moving' | 'medium_moving' | 'fast_moving';
  avg_daily_movement: number;
  primary_image_url?: string;
}

export interface MovementCategoryItems {
  success: boolean;
  auth_method: string;
  category: string;
  date_range: {
    start_date: string;
    end_date: string;
  };
  pagination: {
    limit: number;
    offset: number;
  };
  items: MovementCategoryItem[];
}

export interface DailyMovementData {
  date: string;
  day_name: string;
  day_short: string;
  is_today: boolean;
  movements: {
    in: {
      purchase_in: number;
      adjustment_in: number;
      delivery_return: number;
      opening_stock: number;
      total: number;
    };
    out: {
      delivery_out: number;
      adjustment_out: number;
      total: number;
    };
    net_movement: number;
    total_movements: number;
  };
}

export interface DailyMovementAnalytics {
  success: boolean;
  auth_method: string;
  date_range: {
    start_date: string;
    end_date: string;
    days_count: number;
  };
  summary: {
    is_admin: boolean;
    total_movements_today?: number;
    active_users_today?: number;
    total_stock_value?: number;
    total_items?: number;  // NEW: Proper item count field
  };
  daily_data: DailyMovementData[];
}

export class AnalyticsService {
  /**
   * Get item movement analytics summary
   */
  static async getItemMovementAnalytics(
    userId: string,
    storeId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<MovementAnalytics> {
    try {
      const { data, error } = await supabase.rpc('get_item_movement_analytics', {
        p_user_id: userId,
        p_store_id: storeId || null,
        p_start_date: startDate || null,
        p_end_date: endDate || null
      });

      if (error) {
        console.error('Error fetching movement analytics:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Analytics service error:', error);
      throw error;
    }
  }

  /**
   * Get items by movement category
   */
  static async getItemsByMovementCategory(
    userId: string,
    category: 'no_movement' | 'slow_moving' | 'medium_moving' | 'fast_moving' | 'all' = 'all',
    storeId?: string,
    startDate?: string,
    endDate?: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<MovementCategoryItems> {
    try {
      const { data, error } = await supabase.rpc('get_items_by_movement_category', {
        p_user_id: userId,
        p_store_id: storeId || null,
        p_category: category,
        p_start_date: startDate || null,
        p_end_date: endDate || null,
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        console.error('Error fetching items by category:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Analytics service error:', error);
      throw error;
    }
  }

  /**
   * Format date for RPC calls (YYYY-MM-DD)
   */
  static formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Get default date range (last 90 days)
   */
  static getDefaultDateRange(): { startDate: string; endDate: string } {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 90);

    return {
      startDate: this.formatDate(startDate),
      endDate: this.formatDate(endDate)
    };
  }

  /**
   * Get daily movement analytics
   */
  static async getDailyMovementAnalytics(
    userId: string,
    storeId?: string,
    startDate?: string,
    endDate?: string,
    daysCount: number = 5
  ): Promise<DailyMovementAnalytics> {
    try {
      const { data, error } = await supabase.rpc('get_daily_movement_analytics', {
        p_user_id: userId,
        p_store_id: storeId || null,
        p_start_date: startDate || null,
        p_end_date: endDate || null,
        p_days_count: daysCount
      });

      if (error) {
        console.error('Error fetching daily movement analytics:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Analytics service error:', error);
      throw error;
    }
  }

  /**
   * Get movement type display info
   */
  static getMovementTypeInfo(movementType: string) {
    const typeInfo = {
      purchase_in: {
        name: 'Purchase In',
        description: 'Stock purchased from suppliers',
        color: 'bg-blue-500',
        textColor: 'text-blue-700',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      },
      adjustment_in: {
        name: 'Adjustment In',
        description: 'Positive stock adjustments',
        color: 'bg-green-500',
        textColor: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      },
      delivery_return: {
        name: 'Delivery Return',
        description: 'Items returned from delivery',
        color: 'bg-purple-500',
        textColor: 'text-purple-700',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200'
      },
      opening_stock: {
        name: 'Opening Stock',
        description: 'Initial stock entries',
        color: 'bg-indigo-500',
        textColor: 'text-indigo-700',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-200'
      },
      delivery_out: {
        name: 'Delivery Out',
        description: 'Items sent for delivery',
        color: 'bg-orange-500',
        textColor: 'text-orange-700',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200'
      },
      adjustment_out: {
        name: 'Adjustment Out',
        description: 'Negative stock adjustments',
        color: 'bg-red-500',
        textColor: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      }
    };

    return typeInfo[movementType as keyof typeof typeInfo] || {
      name: 'Unknown',
      description: 'Unknown movement type',
      color: 'bg-gray-500',
      textColor: 'text-gray-700',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200'
    };
  }

  /**
   * Get movement category display info
   */
  static getMovementCategoryInfo(category: string) {
    const categoryInfo = {
      no_movement: {
        name: 'No Movement',
        description: 'Items with no movement in the selected period',
        color: 'bg-red-500',
        textColor: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      },
      slow_moving: {
        name: 'Slow Moving',
        description: 'Items with low movement frequency',
        color: 'bg-orange-500',
        textColor: 'text-orange-700',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200'
      },
      medium_moving: {
        name: 'Medium Moving',
        description: 'Items with moderate movement frequency',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-700',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200'
      },
      fast_moving: {
        name: 'Fast Moving',
        description: 'Items with high movement frequency',
        color: 'bg-green-500',
        textColor: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      }
    };

    return categoryInfo[category as keyof typeof categoryInfo] || categoryInfo.slow_moving;
  }
}
